import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWandMagicSparkles,
  faCheck,
  faSpinner,
  faImage,
  faTimes,
  faImages,
} from '@fortawesome/free-solid-svg-icons';
import { generateAiImage } from '../utils/apis';
import MediaLibrary from './MediaLibrary';
import './ChatImageCollector.css';

/**
 * ChatImageCollector
 *
 * Shown inline in the chat when the AI needs images before it can apply an action.
 * Uses the existing MediaLibrary component for gallery/upload/premium selection.
 *
 * Props:
 *  - imageRequests  {Array}    List of { slot, label, description, generateImages }
 *  - pendingAction  {Object}   The AI action with __IMAGE_PLACEHOLDER_X__ values
 *  - siteContext    {Object}   { site_name, business_type, key_services }
 *  - accountKey     {string}   Subdomain (used for AI generation context)
 *  - onComplete     {Function} Called with the resolved action
 *  - onCancel       {Function} Called when user cancels
 */
function ChatImageCollector({ imageRequests, pendingAction, siteContext = {}, accountKey, onComplete, onCancel }) {
  const [slots, setSlots] = useState(() =>
    imageRequests.map((req) => ({
      slot: req.slot,
      label: req.label,
      description: req.description || '',
      // Pre-fill with existing URL if provided — mark as done so user can apply without changing
      url: req.existingUrl || null,
      status: req.existingUrl ? 'done' : 'idle',
      error: null,
    }))
  );

  const updateSlot = useCallback((slot, updates) => {
    setSlots((prev) => prev.map((s) => (s.slot === slot ? { ...s, ...updates } : s)));
  }, []);

  // Called when MediaLibrary selects an image for a slot
  const handleMediaSelect = useCallback((slot, url) => {
    updateSlot(slot, { url, status: 'done', error: null });
  }, [updateSlot]);

  // Quick AI generate for a single slot
  const handleQuickGenerate = useCallback(async (slot, description) => {
    const desc = description?.trim();
    if (!desc) return;
    updateSlot(slot, { status: 'generating', error: null });
    try {
      const context = siteContext?.business_type
        ? `for a ${siteContext.business_type} website called ${siteContext.site_name || ''}`
        : '';
      const result = await generateAiImage({ description: desc, accountKey, context });
      updateSlot(slot, { url: result.url, status: 'done' });
    } catch (err) {
      updateSlot(slot, { status: 'error', error: 'Generation failed. Try again.' });
    }
  }, [accountKey, siteContext, updateSlot]);

  // Generate all remaining slots at once
  const handleGenerateAll = useCallback(async () => {
    const pending = slots.filter((s) => s.status !== 'done');
    // Run sequentially to avoid hammering the API
    for (const s of pending) {
      await handleQuickGenerate(s.slot, s.description || s.label);
    }
  }, [slots, handleQuickGenerate]);

  /**
   * Replace __IMAGE_PLACEHOLDER_X__ tokens in the pending action with real URLs.
   *
   * FIX: We do deep object traversal instead of JSON string replacement,
   * which avoids the JSON parse/stringify quoting bug that caused "Apply" to do nothing.
   */
  const resolvePendingAction = useCallback(() => {
    const urlMap = {};
    slots.forEach((s) => {
      urlMap[`__IMAGE_PLACEHOLDER_${s.slot}__`] = s.url || 'https://placehold.co/400x300';
    });

    function replacePlaceholders(value) {
      if (typeof value === 'string') {
        // Replace any placeholder token in the string
        return value.replace(/__IMAGE_PLACEHOLDER_(\d+)__/g, (match) => urlMap[match] || 'https://placehold.co/400x300');
      }
      if (Array.isArray(value)) {
        return value.map(replacePlaceholders);
      }
      if (value !== null && typeof value === 'object') {
        const result = {};
        for (const key of Object.keys(value)) {
          result[key] = replacePlaceholders(value[key]);
        }
        return result;
      }
      return value;
    }

    return replacePlaceholders(pendingAction);
  }, [slots, pendingAction]);

  const allDone = slots.every((s) => s.status === 'done');
  const anyBusy = slots.some((s) => s.status === 'generating');
  const doneCount = slots.filter((s) => s.status === 'done').length;

  const handleApply = useCallback(() => {
    const resolved = resolvePendingAction();
    console.log('[AI] Applying resolved action:', resolved); // debug
    onComplete(resolved);
  }, [resolvePendingAction, onComplete]);

  return (
    <div className="chat-image-collector">
      <div className="cic-header">
        <FontAwesomeIcon icon={faImage} className="cic-header-icon" />
        <span>
          {slots.every((s) => s.status === 'done')
            ? `${slots.length} image${slots.length !== 1 ? 's' : ''} ready — keep current or change any.`
            : `${slots.filter(s => s.status !== 'done').length} of ${slots.length} image${slots.length !== 1 ? 's' : ''} still needed.`
          }
        </span>
      </div>

      <div className="cic-slots">
        {slots.map((s) => (
          <div key={s.slot} className={`cic-slot cic-slot--${s.status}`}>

            {/* Preview thumbnail */}
            <div className="cic-slot-preview">
              {s.url ? (
                <img src={s.url} alt={s.label} />
              ) : (
                <div className="cic-slot-placeholder">
                  <FontAwesomeIcon icon={faImage} />
                </div>
              )}
              {s.status === 'done' && (
                <div className="cic-slot-done-badge">
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              )}
              {s.status === 'generating' && (
                <div className="cic-slot-generating-overlay">
                  <FontAwesomeIcon icon={faSpinner} spin />
                </div>
              )}
            </div>

            {/* Slot body */}
            <div className="cic-slot-body">
              <div className="cic-slot-label">{s.label}</div>

              {/* Description input for AI generation */}
              <input
                className="cic-slot-desc"
                type="text"
                placeholder="Describe for AI generation..."
                value={s.description}
                onChange={(e) => updateSlot(s.slot, { description: e.target.value })}
                disabled={s.status === 'generating'}
              />

              {s.error && <div className="cic-slot-error">{s.error}</div>}

              <div className="cic-slot-actions">
                {/*
                  Use the existing MediaLibrary component.
                  Pass selectedUrl so the current image is highlighted.
                  In picker mode (selectedUrl prop provided), MediaLibrary shows
                  a "Use This Image" confirm button instead of closing on click.
                */}
                <div className="cic-media-library-wrap">
                  <MediaLibrary
                    onSelectImage={(url) => handleMediaSelect(s.slot, url)}
                    selectedUrl={s.url}
                    triggerLabel={s.status === 'done' ? 'Change Image' : 'Choose from Gallery'}
                  />
                </div>

                {/* Quick AI generate button */}
                <button
                  className="cic-btn cic-btn--generate"
                  onClick={() => handleQuickGenerate(s.slot, s.description || s.label)}
                  disabled={s.status === 'generating' || !s.description.trim()}
                  title={s.description.trim() ? 'Generate with AI' : 'Enter a description first'}
                >
                  {s.status === 'generating' ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    <FontAwesomeIcon icon={faWandMagicSparkles} />
                  )}
                  <span>AI Generate</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Generate all remaining with AI */}
      {slots.length > 1 && !allDone && (
        <button
          className="cic-generate-all"
          onClick={handleGenerateAll}
          disabled={anyBusy}
        >
          {anyBusy ? (
            <><FontAwesomeIcon icon={faSpinner} spin /> Generating...</>
          ) : (
            <><FontAwesomeIcon icon={faWandMagicSparkles} /> Generate all {slots.length} with AI</>
          )}
        </button>
      )}

      {/* Footer */}
      <div className="cic-footer">
        <button className="cic-btn-cancel" onClick={onCancel}>
          <FontAwesomeIcon icon={faTimes} /> Cancel
        </button>
        <button
          className="cic-btn-apply"
          onClick={handleApply}
          disabled={!allDone || anyBusy}
        >
          {allDone ? (
            <><FontAwesomeIcon icon={faCheck} /> Apply to Page</>
          ) : (
            `${doneCount}/${slots.length} images ready`
          )}
        </button>
      </div>
    </div>
  );
}

export default ChatImageCollector;
